import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { 
  Download, 
  FileText, 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  ChevronLeft, 
  ChevronRight,
  Maximize,
  FileDown
} from 'lucide-react';

const PDFViewer = ({ file, content, projectId, myProfile, onDownload, previewUrl }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleFullscreen = () => {
    if (iframeRef.current) {
      if (!isFullscreen) {
        iframeRef.current.requestFullscreen?.();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen?.();
        setIsFullscreen(false);
      }
    }
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="space-y-4">
      {/* PDF Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <FileText className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-gray-900">{file.original_filename}</h3>
            <p className="text-sm text-gray-500">
              PDF Document • {Math.round(file.file_size / 1024)} KB
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onDownload}
          >
            <Download className="h-4 w-4 mr-1" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* PDF Controls */}
      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevPage}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <span className="px-3 py-1 bg-white rounded text-sm font-medium">
            Page {currentPage} of {totalPages}
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          
          <span className="px-2 py-1 bg-white rounded text-sm font-medium min-w-[60px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= 3.0}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleRotate}
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleFullscreen}
          >
            <Maximize className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="border rounded-lg overflow-hidden bg-gray-100">
        {previewUrl ? (
          <div 
            style={{ 
              height: '600px',
              overflow: 'auto'
            }}
          >
            <iframe
              ref={iframeRef}
              src={`${previewUrl}#page=${currentPage}&zoom=${zoom * 100}&toolbar=1&navpanes=0`}
              className="w-full h-full border-none"
              title="PDF Preview"
              style={{
                transform: `rotate(${rotation}deg) scale(${zoom})`,
                transformOrigin: 'center center'
              }}
              onLoad={() => {
                // In a real implementation, you'd extract page count from PDF
                setTotalPages(1); // Placeholder
              }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center" style={{ height: '600px' }}>
            <div className="text-center">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Loading PDF...</p>
            </div>
          </div>
        )}
      </div>

      {/* PDF Information */}
      <div className="flex items-center justify-between text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center gap-4">
          <span>Document: PDF</span>
          <span>Size: {Math.round(file.file_size / 1024)} KB</span>
          <span>Zoom: {Math.round(zoom * 100)}%</span>
          {rotation > 0 && <span>Rotated: {rotation}°</span>}
        </div>
        <div>
          <span className="text-blue-600">● PDF Viewer</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setZoom(1.0)}
          className="text-xs"
        >
          Reset Zoom
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setRotation(0)}
          className="text-xs"
        >
          Reset Rotation
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setCurrentPage(1)}
          className="text-xs"
        >
          First Page
        </Button>
      </div>
    </div>
  );
};

export default PDFViewer;